import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import VisitCard from "../../components/visits/VisitCard";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import { Sparkles, Trophy, Inbox, X } from "lucide-react";
import StatCard from "../../components/dashboard/StatCard";
import {
  CalendarDays,
  CalendarRange,
  CalendarCheck,
} from "lucide-react";
import { FEEDBACK_OPTIONS } from "../../lib/visitOptions";
import { todayIST, daysAgoIST, currentMonthIST, monthRangeIST } from "../../lib/datetime";
import StickyHeading from "../../components/common/StickyHeading";
import DeviationCounts from "../../components/visits/DeviationCounts";

const ALL = "__all__";

export default function AllVisitsPage() {
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState({ today: 0, week: 0, month: 0 });
  const [topSos, setTopSos] = useState([]);
  const [sos, setSos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [soId, setSoId] = useState(ALL);
  const [feedback, setFeedback] = useState(ALL);
  const [product, setProduct] = useState(ALL);
  const [products, setProducts] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Stat counts (today/week/month) — use lightweight count queries.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The month boundary is the company's, not the viewer's: on the 1st
        // before 05:30 IST a local-midnight `new Date()` is still last month.
        const { year, month } = currentMonthIST();
        const monthIso = monthRangeIST(year, month).first;
        const week = daysAgoIST(6);
        const tIso = todayIST();
        const [{ data: monthRows }, { data: todayRows }, { data: weekRows }] = await Promise.all([
          api.get("/doctor-visits", { params: { date_from: monthIso } }),
          api.get("/doctor-visits", { params: { date_from: tIso } }),
          api.get("/doctor-visits", { params: { date_from: week } }),
        ]);
        if (cancelled) return;
        setStats({ today: todayRows.length, week: weekRows.length, month: monthRows.length });
        // Top SOs by month visit count.
        const map = {};
        for (const v of monthRows) {
          const k = v.sales_officer_id;
          map[k] = map[k] || { id: k, name: v.sales_officer_name, count: 0 };
          map[k].count += 1;
        }
        setTopSos(
          Object.values(map)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5)
        );
      } catch (_) {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // SO list for filter.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        // Sales Managers log their own calls, so they appear in the filter too.
        setSos(
          data.filter(
            (u) => ["sales_officer", "assistant_manager"].includes(u.role) && u.is_active
          )
        );
      } catch (_) {
        /* non-fatal */
      }
    })();
  }, []);

  // Product filter options come from the live master, matching the DCR form.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/doctor-visits/meta/products");
        setProducts(data.products || []);
      } catch (_) {
        /* non-fatal */
      }
    })();
  }, []);

  // Filtered list.
  useEffect(() => {
    const params = {};
    if (soId !== ALL) params.so_id = soId;
    if (feedback !== ALL) params.feedback = feedback;
    if (product !== ALL) params.product = product;
    if (from) params.date_from = from;
    if (to) params.date_to = to;

    let cancelled = false;
    setLoading(true);
    api
      .get("/doctor-visits", { params })
      .then(({ data }) => !cancelled && setVisits(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [soId, feedback, product, from, to]);

  const hasFilters =
    soId !== ALL || feedback !== ALL || product !== ALL || !!from || !!to;
  const clear = () => {
    setSoId(ALL);
    setFeedback(ALL);
    setProduct(ALL);
    setFrom("");
    setTo("");
  };

  return (
    <div className="space-y-6" data-testid="all-visits-page">
      <StickyHeading
        icon={Sparkles}
        label="Company-wide DCR"
        title="All visits"
        subtitle="The complete activity log across every sales officer and manager."
        testId="all-visits-heading"
      />

      {/* Headline stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          testId="all-stat-today"
          label="Today"
          value={stats.today}
          helper="Visits company-wide"
          Icon={CalendarDays}
        />
        <StatCard
          testId="all-stat-week"
          label="This Week"
          value={stats.week}
          helper="Last 7 days"
          Icon={CalendarRange}
        />
        <StatCard
          testId="all-stat-month"
          label="This Month"
          value={stats.month}
          helper="From the 1st"
          Icon={CalendarCheck}
          accent
        />
      </div>

      {/* Top SOs */}
      <div className="bg-white border border-callisto rounded-xl p-5">
        <p className="text-base font-semibold text-callisto-navy flex items-center gap-2">
          <Trophy className="h-4 w-4 text-callisto-gold" />
          Top field staff · This month
        </p>
        {topSos.length === 0 ? (
          <p className="mt-2 text-sm text-callisto-muted">
            No visits this month yet — leaderboard will populate as your team logs DCRs.
          </p>
        ) : (
          <ol className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
            {topSos.map((u, idx) => (
              <li
                key={u.id}
                className="flex items-center gap-2 rounded-md px-2.5 py-2 bg-callisto-app"
              >
                <span className="h-6 w-6 shrink-0 rounded-full bg-callisto-navy text-white text-[11px] font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                <span className="text-sm font-semibold text-callisto-navy truncate flex-1">
                  {u.name}
                </span>
                <span className="text-sm font-bold text-callisto-navy">{u.count}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Plan deviation (§2 Session 4): the same four numbers the Sales
          Manager sees, company-wide — one component, one detector. */}
      <div data-testid="all-plan-deviation">
        <DeviationCounts
          from={from}
          to={to}
          soId={soId !== ALL ? soId : undefined}
          testIdPrefix="all-deviation"
        />
      </div>

      {/* Filters */}
      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Select value={soId} onValueChange={setSoId}>
            <SelectTrigger className="h-11 col-span-2 md:col-span-1 focus:ring-callisto-gold" data-testid="all-filter-so">
              <SelectValue placeholder="Officer / manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All officers &amp; managers</SelectItem>
              {sos.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={feedback} onValueChange={setFeedback}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-filter-feedback">
              <SelectValue placeholder="Feedback" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All feedback</SelectItem>
              {FEEDBACK_OPTIONS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-filter-product">
              <SelectValue placeholder="Product" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All products</SelectItem>
              {products.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={to || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-filter-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-filter-to"
          />
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            onClick={clear}
            className="mt-2 h-9 text-callisto-muted hover:text-callisto-navy"
            data-testid="all-filters-clear"
          >
            <X className="h-3 w-3 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-36 w-full rounded-xl" />
          ))}
        </div>
      ) : visits.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">No visits match</p>
          <p className="mt-1 text-xs text-callisto-muted">Try widening the filters.</p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
          data-testid="all-visits-grid"
        >
          {visits.map((v) => (
            <VisitCard key={v.id} visit={v} showOfficer />
          ))}
        </div>
      )}
    </div>
  );
}
